/**
 * Control Room: Oversikt og Organisasjoner (sak 238, trinn 3) — `/v1/platform/overview` og
 * `/v1/platform/organizations…`. Plattformens tall, utenfor hvert hus: ingen organisasjonsheader,
 * og nøklene er globale (`qk.controlRoom…`). Lista og brukerlista blar med markør.
 */
import {
  controlRoomOrganizationDetailSchema,
  controlRoomOrganizationListResponseSchema,
  controlRoomOrganizationUserListResponseSchema,
  controlRoomOverviewSchema,
  controlRoomPeopleWithoutOrganizationResponseSchema,
  type ControlRoomPeopleWithoutOrganizationResponse,
  type ControlRoomOrganizationDetail,
  type ControlRoomOrganizationListResponse,
  type ControlRoomOrganizationSortKey,
  type ControlRoomOrganizationUserListResponse,
  type ControlRoomOverview,
  type SortDirection,
} from '@tvitvi/shared';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const ORGANIZATIONS = '/platform/organizations';
const PAGE_SIZE = 100;

export interface OrganizationListFilters {
  search?: string;
  sort: ControlRoomOrganizationSortKey;
  direction: SortDirection;
  /** F58: de arkiverte i stedet for de vanlige. */
  archived?: boolean;
}

export function controlRoomOverviewQueryOptions() {
  return queryOptions({
    queryKey: qk.controlRoomOverview(),
    queryFn: ({ signal }): Promise<ControlRoomOverview> =>
      apiRequest({
        path: '/platform/overview',
        schema: controlRoomOverviewSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    ...CACHE.list,
  });
}

export function controlRoomOrganizationsQueryOptions(filters: OrganizationListFilters) {
  return infiniteQueryOptions({
    queryKey: qk.controlRoomOrganizationList({ ...filters }),
    queryFn: ({ pageParam, signal }): Promise<ControlRoomOrganizationListResponse> =>
      apiRequest({
        path: ORGANIZATIONS,
        query: {
          sort: filters.sort,
          direction: filters.direction,
          limit: PAGE_SIZE,
          ...(filters.search === undefined || filters.search === ''
            ? {}
            : { search: filters.search }),
          ...(filters.archived === true ? { archived: 'true' } : {}),
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        schema: controlRoomOrganizationListResponseSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}

export function controlRoomOrganizationQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: qk.controlRoomOrganization(organizationId),
    queryFn: ({ signal }): Promise<ControlRoomOrganizationDetail> =>
      apiRequest({
        path: `${ORGANIZATIONS}/${organizationId}`,
        schema: controlRoomOrganizationDetailSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    ...CACHE.detail,
  });
}

export function controlRoomOrganizationUsersQueryOptions(organizationId: string) {
  return infiniteQueryOptions({
    queryKey: qk.controlRoomOrganizationUsers(organizationId),
    queryFn: ({ pageParam, signal }): Promise<ControlRoomOrganizationUserListResponse> =>
      apiRequest({
        path: `${ORGANIZATIONS}/${organizationId}/users`,
        query: { limit: PAGE_SIZE, ...(pageParam === null ? {} : { cursor: pageParam }) },
        schema: controlRoomOrganizationUserListResponseSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}

/** 253.41: «Brukere uten organisasjon», sortert på navn, med markør. */
export function controlRoomPeopleWithoutOrganizationQueryOptions() {
  return infiniteQueryOptions({
    queryKey: qk.controlRoomPeopleWithoutOrganization(),
    queryFn: ({ pageParam, signal }): Promise<ControlRoomPeopleWithoutOrganizationResponse> =>
      apiRequest({
        path: '/platform/people-without-organization',
        query: { limit: PAGE_SIZE, ...(pageParam === null ? {} : { cursor: pageParam }) },
        schema: controlRoomPeopleWithoutOrganizationResponseSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}
